import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Button,
    Input,
    Textarea,
    Typography,
    Checkbox
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';

const StatusChangeModal = ({ open, onClose, currentStatus, newStatus, onConfirm, user }) => {
    const [notes, setNotes] = useState('');
    const [invoiceReference, setInvoiceReference] = useState('');
    const [invoiceSkipped, setInvoiceSkipped] = useState(false);
    const [invoiceSkipNotes, setInvoiceSkipNotes] = useState('');
    const [driveLink, setDriveLink] = useState('');
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (open) {
            // Reset form when modal opens
            setNotes('');
            setInvoiceReference('');
            setInvoiceSkipped(false);
            setInvoiceSkipNotes('');
            setDriveLink('');
            setErrors({});
        }
    }, [open]);

    const validate = () => {
        const newErrors = {};

        // Notes are always required
        if (!notes || !notes.trim()) {
            newErrors.notes = 'Notes are required when changing status';
        }

        // For invoiced status
        if (newStatus === 'invoiced') {
            if (!invoiceReference && !invoiceSkipped) {
                newErrors.invoice = 'Either provide invoice reference or skip invoicing';
            }
            if (invoiceSkipped && !invoiceSkipNotes.trim()) {
                newErrors.invoiceSkipNotes = 'Notes are required when skipping invoicing';
            }
        }

        // For closed status
        if (newStatus === 'closed') {
            if (!driveLink || !driveLink.trim()) {
                newErrors.driveLink = 'Drive link is required before closing';
            } else {
                try {
                    new URL(driveLink);
                } catch (e) {
                    newErrors.driveLink = 'Please provide a valid URL';
                }
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleConfirm = () => {
        if (!validate()) {
            return;
        }

        const data = {
            notes,
            invoice_reference: invoiceReference || null,
            invoice_skipped: invoiceSkipped,
            invoice_skip_notes: invoiceSkipped ? invoiceSkipNotes : null,
            drive_link: driveLink || null
        };

        onConfirm(data);
    };

    const getStatusLabel = (status) => {
        const labels = {
            'open': 'Open',
            'in_progress': 'In Progress',
            'completed': 'Completed',
            'invoiced': 'Invoiced',
            'closed': 'Closed'
        };
        return labels[status] || status;
    };

    return (
        <Dialog open={open} handler={onClose} size="lg">
            <DialogHeader>
                <Typography variant="h5">
                    Change Status: {getStatusLabel(currentStatus)} → {getStatusLabel(newStatus)}
                </Typography>
            </DialogHeader>
            <DialogBody className="space-y-4">
                <div>
                    <Textarea
                        label="Notes *"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={4}
                        error={!!errors.notes}
                        required
                    />
                    {errors.notes && (
                        <Typography variant="small" color="red" className="mt-1">
                            {errors.notes}
                        </Typography>
                    )}
                    <Typography variant="small" color="gray" className="mt-1">
                        Please provide notes explaining this status change
                    </Typography>
                </div>

                {newStatus === 'invoiced' && (
                    <>
                        <div>
                            <Input
                                label="Invoice Reference"
                                value={invoiceReference}
                                onChange={(e) => {
                                    setInvoiceReference(e.target.value);
                                    if (e.target.value) {
                                        setInvoiceSkipped(false);
                                    }
                                }}
                                disabled={invoiceSkipped}
                            />
                        </div>

                        <div>
                            <Checkbox
                                label="Skip Invoicing"
                                checked={invoiceSkipped}
                                onChange={(e) => {
                                    setInvoiceSkipped(e.target.checked);
                                    if (e.target.checked) {
                                        setInvoiceReference('');
                                    }
                                }}
                            />
                        </div>

                        {invoiceSkipped && (
                            <div>
                                <Textarea
                                    label="Skip Invoicing Notes *"
                                    value={invoiceSkipNotes}
                                    onChange={(e) => setInvoiceSkipNotes(e.target.value)}
                                    rows={3}
                                    error={!!errors.invoiceSkipNotes}
                                    required
                                />
                                {errors.invoiceSkipNotes && (
                                    <Typography variant="small" color="red" className="mt-1">
                                        {errors.invoiceSkipNotes}
                                    </Typography>
                                )}
                            </div>
                        )}

                        {errors.invoice && (
                            <Typography variant="small" color="red">
                                {errors.invoice}
                            </Typography>
                        )}
                    </>
                )}

                {newStatus === 'closed' && (
                    <div>
                        <Input
                            label="Drive Link *"
                            value={driveLink}
                            onChange={(e) => setDriveLink(e.target.value)}
                            error={!!errors.driveLink}
                            required
                            placeholder="https://drive.google.com/..."
                        />
                        {errors.driveLink && (
                            <Typography variant="small" color="red" className="mt-1">
                                {errors.driveLink}
                            </Typography>
                        )}
                        <Typography variant="small" color="gray" className="mt-1">
                            Please provide a valid Google Drive link to the filled job card
                        </Typography>
                    </div>
                )}
            </DialogBody>
            <DialogFooter>
                <Button
                    variant="text"
                    color="red"
                    onClick={onClose}
                    className="mr-1"
                >
                    <FontAwesomeIcon icon={faTimes} className="mr-2" />
                    Cancel
                </Button>
                <Button
                    color="green"
                    onClick={handleConfirm}
                >
                    <FontAwesomeIcon icon={faCheck} className="mr-2" />
                    Confirm Status Change
                </Button>
            </DialogFooter>
        </Dialog>
    );
};

export default StatusChangeModal;

